import type {
  AcompteClient,
  AcompteCreateBody,
  AcompteLierVenteBody,
  AcompteUpdateBody,
  AcomptesListResponse,
  AlertesStockResponse,
  Avoir,
  AvoirCreateBody,
  AvoirDetail,
  AvoirStatutPatchBody,
  AvoirUpdateBody,
  AvoirsListResponse,
  CAByClientTypeResponse,
  CAByUserResponse,
  CAMonthlyResponse,
  CashflowResponse,
  ChargeFixe,
  ChargeFixeCreateBody,
  ChargeFixeUpdateBody,
  ChargesFixesListResponse,
  Container,
  ContainerCreateBody,
  ContainerDetail,
  ContainerUpdateBody,
  ContainersListResponse,
  Contact,
  ContactCreateBody,
  ContactDetailResponse,
  ContactLegerBody,
  ContactUpdateBody,
  ContactsListResponse,
  CsvCommitBody,
  CsvCommitResponse,
  CsvContactsCommitBody,
  CsvContactsCommitResponse,
  CsvContactsPreviewResponse,
  CsvPreviewResponse,
  DashboardKpisResponse,
  DashboardTresorerieResponse,
  DetteGlobalWood,
  DetteGlobalWoodInitBody,
  DetteGlobalWoodSummary,
  DetteGlobalWoodUpdateBody,
  Echeance,
  EcheanceCreateBody,
  EcheanceUpdateBody,
  EcheanceWizardBody,
  EcheanceWizardResponse,
  EcheancesResponse,
  EncaissementsListResponse,
  EncaissementsStatsResponse,
  HealthResponse,
  ImportSaisieResponse,
  Interaction,
  InteractionCreateBody,
  InteractionUpdateBody,
  InteractionsListResponse,
  MargesParReferenceResponse,
  PaiementClient,
  PaiementClientCreateBody,
  PaiementClientUpdateBody,
  PaiementClientsResponse,
  PaiementGlobalWood,
  PaiementGlobalWoodCreateBody,
  PaiementGlobalWoodUpdateBody,
  PaiementsGlobalWoodListResponse,
  PipelineResponse,
  PipelineValueResponse,
  ReceptionResponse,
  ReferenceCreateBody,
  ReferenceListItem,
  ReferenceProduit,
  ReferenceUpdateBody,
  ReferencesListResponse,
  RouleauDetail,
  RouleauxListResponse,
  StatutPipeline,
  StockValueResponse,
  TopClientsResponse,
  User,
  VenteAvecLignes,
  VenteCreateBody,
  VenteUpdateBody,
  VentesListResponse,
} from '@cockpit/shared';

class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  init?: RequestInit & { params?: Record<string, string | number | undefined> },
): Promise<T> {
  let url = path;
  if (init?.params !== undefined) {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(init.params)) {
      if (v !== undefined && v !== '') search.set(k, String(v));
    }
    const qs = search.toString();
    if (qs !== '') url += `?${qs}`;
  }

  const response = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  // 204 No Content
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  let body: unknown = null;
  const text = await response.text();
  if (text !== '') {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!response.ok) {
    const details =
      body !== null && typeof body === 'object'
        ? (body as { error?: string; details?: unknown })
        : undefined;
    throw new ApiError(
      details?.error ?? `HTTP ${String(response.status)} sur ${path}`,
      response.status,
      details?.details,
    );
  }

  return body as T;
}

export const api = {
  health(): Promise<HealthResponse> {
    return request<HealthResponse>('/api/health');
  },

  auth: {
    login(body: { username: string; password: string }): Promise<{ user: User }> {
      return request<{ user: User }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    logout(): Promise<void> {
      return request<void>('/api/auth/logout', { method: 'POST' });
    },
    me(): Promise<{ user: User }> {
      return request<{ user: User }>('/api/auth/me');
    },
  },

  ventes: {
    list(
      params: Partial<{
        period_from: string;
        period_to: string;
        user_id: number;
        contact_id: number;
        statut_paiement: string;
        type_client: string;
        page: number;
        page_size: number;
      }> = {},
    ): Promise<VentesListResponse> {
      return request<VentesListResponse>('/api/ventes', { params });
    },
    get(id: number): Promise<VenteAvecLignes> {
      return request<VenteAvecLignes>(`/api/ventes/${String(id)}`);
    },
    create(body: VenteCreateBody): Promise<VenteAvecLignes> {
      return request<VenteAvecLignes>('/api/ventes', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    update(id: number, body: VenteUpdateBody): Promise<VenteAvecLignes> {
      return request<VenteAvecLignes>(`/api/ventes/${String(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    delete(id: number): Promise<void> {
      return request<void>(`/api/ventes/${String(id)}`, { method: 'DELETE' });
    },
  },

  contacts: {
    list(
      params: {
        q?: string;
        territoire?: string;
        type_client?: string;
        statut_pipeline?: string;
        source?: string;
        departement?: string;
        tag?: string;
        deleted?: 'true' | 'false';
        sort?: 'nom_asc' | 'nom_desc' | 'derniere_interaction_desc' | 'created_at_desc';
        page?: number;
        page_size?: number;
      } = {},
    ): Promise<ContactsListResponse> {
      return request<ContactsListResponse>('/api/contacts', { params });
    },
    get(id: number): Promise<ContactDetailResponse> {
      return request<ContactDetailResponse>(`/api/contacts/${String(id)}`);
    },
    create(body: ContactCreateBody): Promise<{ contact: Contact }> {
      return request<{ contact: Contact }>('/api/contacts', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    update(id: number, body: ContactUpdateBody): Promise<{ contact: Contact }> {
      return request<{ contact: Contact }>(`/api/contacts/${String(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    delete(id: number): Promise<void> {
      return request<void>(`/api/contacts/${String(id)}`, { method: 'DELETE' });
    },
    restore(id: number): Promise<{ contact: Contact }> {
      return request<{ contact: Contact }>(`/api/contacts/${String(id)}/restore`, {
        method: 'POST',
      });
    },
    pipelineMove(id: number, statut: StatutPipeline): Promise<{ contact: Contact }> {
      return request<{ contact: Contact }>(`/api/contacts/${String(id)}/pipeline-move`, {
        method: 'POST',
        body: JSON.stringify({ statut_pipeline: statut }),
      });
    },
    pipeline(
      params: { territoire?: string; type_client?: string; source?: string } = {},
    ): Promise<PipelineResponse> {
      return request<PipelineResponse>('/api/contacts/pipeline', { params });
    },
    relances(params: { user_id?: number; date_max?: string } = {}): Promise<ContactsListResponse> {
      return request<ContactsListResponse>('/api/contacts/relances', { params });
    },
    tags(): Promise<{ tags: string[] }> {
      return request<{ tags: string[] }>('/api/contacts/tags');
    },
    addTag(id: number, label: string): Promise<{ label: string }> {
      return request<{ label: string }>(`/api/contacts/${String(id)}/tags`, {
        method: 'POST',
        body: JSON.stringify({ label }),
      });
    },
    removeTag(id: number, label: string): Promise<void> {
      return request<void>(`/api/contacts/${String(id)}/tags/${encodeURIComponent(label)}`, {
        method: 'DELETE',
      });
    },
    listInteractions(id: number): Promise<InteractionsListResponse> {
      return request<InteractionsListResponse>(`/api/contacts/${String(id)}/interactions`);
    },
    csvPreview(csv: string): Promise<CsvContactsPreviewResponse> {
      return request<CsvContactsPreviewResponse>('/api/contacts/import/preview', {
        method: 'POST',
        body: JSON.stringify({ csv }),
      });
    },
    csvCommit(body: CsvContactsCommitBody): Promise<CsvContactsCommitResponse> {
      return request<CsvContactsCommitResponse>('/api/contacts/import/commit', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    createLeger(
      body: ContactLegerBody,
    ): Promise<{ contact: ContactsListResponse['items'][number] }> {
      return request<{ contact: ContactsListResponse['items'][number] }>('/api/contacts/leger', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
  },

  interactions: {
    create(body: InteractionCreateBody): Promise<{ interaction: Interaction }> {
      return request<{ interaction: Interaction }>('/api/interactions', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    update(id: number, body: InteractionUpdateBody): Promise<{ interaction: Interaction }> {
      return request<{ interaction: Interaction }>(`/api/interactions/${String(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    delete(id: number): Promise<void> {
      return request<void>(`/api/interactions/${String(id)}`, { method: 'DELETE' });
    },
  },

  dashboard: {
    kpis(
      params: { period_from?: string; period_to?: string; user_id?: number } = {},
    ): Promise<DashboardKpisResponse> {
      return request<DashboardKpisResponse>('/api/dashboard/kpis', { params });
    },
    caMonthly(params: { year: number; user_id?: number }): Promise<CAMonthlyResponse> {
      return request<CAMonthlyResponse>('/api/dashboard/ca-monthly', { params });
    },
    caByClientType(
      params: { period_from?: string; period_to?: string; user_id?: number } = {},
    ): Promise<CAByClientTypeResponse> {
      return request<CAByClientTypeResponse>('/api/dashboard/ca-by-client-type', { params });
    },
    caByUser(params: { period_from?: string; period_to?: string } = {}): Promise<CAByUserResponse> {
      return request<CAByUserResponse>('/api/dashboard/ca-by-user', { params });
    },
    topClients(
      params: { period_from?: string; period_to?: string; user_id?: number; limit?: number } = {},
    ): Promise<TopClientsResponse> {
      return request<TopClientsResponse>('/api/dashboard/top-clients', { params });
    },
    pipelineValue(params: { user_id?: number } = {}): Promise<PipelineValueResponse> {
      return request<PipelineValueResponse>('/api/dashboard/pipeline-value', { params });
    },
    stockValue(): Promise<StockValueResponse> {
      return request<StockValueResponse>('/api/dashboard/stock-value');
    },
    alertesStock(): Promise<AlertesStockResponse> {
      return request<AlertesStockResponse>('/api/dashboard/alertes-stock');
    },
    margesParReference(
      params: { period_from?: string; period_to?: string } = {},
    ): Promise<MargesParReferenceResponse> {
      return request<MargesParReferenceResponse>('/api/dashboard/marges-par-reference', { params });
    },
    tresorerie(): Promise<DashboardTresorerieResponse> {
      return request<DashboardTresorerieResponse>('/api/dashboard/tresorerie');
    },
  },

  references: {
    list(
      params: { q?: string; seuil_alerte_depasse?: 'true' | 'false' } = {},
    ): Promise<ReferencesListResponse> {
      return request<ReferencesListResponse>('/api/references', { params });
    },
    get(id: number): Promise<{ reference: ReferenceListItem }> {
      return request<{ reference: ReferenceListItem }>(`/api/references/${String(id)}`);
    },
    create(body: ReferenceCreateBody): Promise<{ reference: ReferenceProduit }> {
      return request<{ reference: ReferenceProduit }>('/api/references', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    update(id: number, body: ReferenceUpdateBody): Promise<{ reference: ReferenceProduit }> {
      return request<{ reference: ReferenceProduit }>(`/api/references/${String(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    delete(id: number): Promise<void> {
      return request<void>(`/api/references/${String(id)}`, { method: 'DELETE' });
    },
  },

  containers: {
    list(
      params: {
        statut?: string;
        finance_global_wood?: 'true' | 'false';
        date_commande_from?: string;
        date_commande_to?: string;
        page?: number;
        page_size?: number;
      } = {},
    ): Promise<ContainersListResponse> {
      return request<ContainersListResponse>('/api/containers', { params });
    },
    get(id: number): Promise<ContainerDetail> {
      return request<ContainerDetail>(`/api/containers/${String(id)}`);
    },
    create(body: ContainerCreateBody): Promise<{ container: Container }> {
      return request<{ container: Container }>('/api/containers', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    update(id: number, body: ContainerUpdateBody): Promise<{ container: Container }> {
      return request<{ container: Container }>(`/api/containers/${String(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    delete(id: number): Promise<void> {
      return request<void>(`/api/containers/${String(id)}`, { method: 'DELETE' });
    },
    receptionner(id: number): Promise<ReceptionResponse> {
      return request<ReceptionResponse>(`/api/containers/${String(id)}/receptionner`, {
        method: 'POST',
      });
    },
  },

  rouleaux: {
    list(
      params: {
        reference_id?: number;
        container_id?: number;
        statut?: string;
        statut_disponible?: 'true' | 'false';
        largeur_cm?: 200 | 400;
      } = {},
    ): Promise<RouleauxListResponse> {
      return request<RouleauxListResponse>('/api/rouleaux', { params });
    },
    get(id: number): Promise<RouleauDetail> {
      return request<RouleauDetail>(`/api/rouleaux/${String(id)}`);
    },
  },

  ventesAdmin: {
    recalcMarges(): Promise<{ ventes_recalculees: number; lignes_recalculees: number }> {
      return request<{ ventes_recalculees: number; lignes_recalculees: number }>(
        '/api/ventes/recalc-marges',
        { method: 'POST' },
      );
    },
  },

  detteGlobalWood: {
    summary(): Promise<DetteGlobalWoodSummary> {
      return request<DetteGlobalWoodSummary>('/api/dette-global-wood');
    },
    init(body: DetteGlobalWoodInitBody): Promise<{ dette: DetteGlobalWood }> {
      return request<{ dette: DetteGlobalWood }>('/api/dette-global-wood/init', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    update(body: DetteGlobalWoodUpdateBody): Promise<{ dette: DetteGlobalWood }> {
      return request<{ dette: DetteGlobalWood }>('/api/dette-global-wood', {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
  },

  paiementsGlobalWood: {
    list(
      params: { container_id?: number; date_from?: string; date_to?: string } = {},
    ): Promise<PaiementsGlobalWoodListResponse> {
      return request<PaiementsGlobalWoodListResponse>('/api/paiements-global-wood', { params });
    },
    create(body: PaiementGlobalWoodCreateBody): Promise<{ paiement: PaiementGlobalWood }> {
      return request<{ paiement: PaiementGlobalWood }>('/api/paiements-global-wood', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    update(
      id: number,
      body: PaiementGlobalWoodUpdateBody,
    ): Promise<{ paiement: PaiementGlobalWood }> {
      return request<{ paiement: PaiementGlobalWood }>(`/api/paiements-global-wood/${String(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    delete(id: number): Promise<void> {
      return request<void>(`/api/paiements-global-wood/${String(id)}`, { method: 'DELETE' });
    },
  },

  tresorerie: {
    encaissements(
      params: {
        statut_effectif?: string;
        contact_id?: number;
        echeance_from?: string;
        echeance_to?: string;
      } = {},
    ): Promise<EncaissementsListResponse> {
      return request<EncaissementsListResponse>('/api/tresorerie/encaissements', { params });
    },
    encaissementsStats(): Promise<EncaissementsStatsResponse> {
      return request<EncaissementsStatsResponse>('/api/tresorerie/encaissements/stats');
    },
    cashflow(
      params: { months?: number; include_pipeline?: 'true' | 'false' } = {},
    ): Promise<CashflowResponse> {
      return request<CashflowResponse>('/api/tresorerie/cashflow', { params });
    },
  },

  paiementsClients: {
    list(venteId: number): Promise<PaiementClientsResponse> {
      return request<PaiementClientsResponse>('/api/paiements-clients', {
        params: { vente_id: venteId },
      });
    },
    create(body: PaiementClientCreateBody): Promise<{ paiement: PaiementClient }> {
      return request<{ paiement: PaiementClient }>('/api/paiements-clients', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    update(id: number, body: PaiementClientUpdateBody): Promise<{ paiement: PaiementClient }> {
      return request<{ paiement: PaiementClient }>(`/api/paiements-clients/${String(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    delete(id: number): Promise<void> {
      return request<void>(`/api/paiements-clients/${String(id)}`, { method: 'DELETE' });
    },
  },

  acomptes: {
    list(
      params: {
        contact_id?: number;
        devis_id?: number;
        non_rattaches?: 'true' | 'false';
        date_from?: string;
        date_to?: string;
      } = {},
    ): Promise<AcomptesListResponse> {
      return request<AcomptesListResponse>('/api/acomptes', { params });
    },
    get(id: number): Promise<{ acompte: AcompteClient }> {
      return request<{ acompte: AcompteClient }>(`/api/acomptes/${String(id)}`);
    },
    create(body: AcompteCreateBody): Promise<{ acompte: AcompteClient }> {
      return request<{ acompte: AcompteClient }>('/api/acomptes', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    update(id: number, body: AcompteUpdateBody): Promise<{ acompte: AcompteClient }> {
      return request<{ acompte: AcompteClient }>(`/api/acomptes/${String(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    lierVente(id: number, body: AcompteLierVenteBody): Promise<{ acompte: AcompteClient }> {
      return request<{ acompte: AcompteClient }>(`/api/acomptes/${String(id)}/lier-vente`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    delete(id: number): Promise<void> {
      return request<void>(`/api/acomptes/${String(id)}`, { method: 'DELETE' });
    },
  },

  avoirs: {
    list(
      params: {
        vente_id?: number;
        contact_id?: number;
        statut?: string;
        type_avoir?: string;
        date_from?: string;
        date_to?: string;
      } = {},
    ): Promise<AvoirsListResponse> {
      return request<AvoirsListResponse>('/api/avoirs', { params });
    },
    get(id: number): Promise<AvoirDetail> {
      return request<AvoirDetail>(`/api/avoirs/${String(id)}`);
    },
    create(body: AvoirCreateBody): Promise<AvoirDetail> {
      return request<AvoirDetail>('/api/avoirs', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    update(id: number, body: AvoirUpdateBody): Promise<AvoirDetail> {
      return request<AvoirDetail>(`/api/avoirs/${String(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    changerStatut(id: number, body: AvoirStatutPatchBody): Promise<{ avoir: Avoir }> {
      return request<{ avoir: Avoir }>(`/api/avoirs/${String(id)}/statut`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    delete(id: number): Promise<void> {
      return request<void>(`/api/avoirs/${String(id)}`, { method: 'DELETE' });
    },
  },

  echeances: {
    list(venteId: number): Promise<EcheancesResponse> {
      return request<EcheancesResponse>('/api/echeances', {
        params: { vente_id: venteId },
      });
    },
    create(body: EcheanceCreateBody): Promise<{ echeance: Echeance }> {
      return request<{ echeance: Echeance }>('/api/echeances', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    update(id: number, body: EcheanceUpdateBody): Promise<{ echeance: Echeance }> {
      return request<{ echeance: Echeance }>(`/api/echeances/${String(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    delete(id: number): Promise<void> {
      return request<void>(`/api/echeances/${String(id)}`, { method: 'DELETE' });
    },
    wizard(venteId: number, body: EcheanceWizardBody): Promise<EcheanceWizardResponse> {
      return request<EcheanceWizardResponse>(`/api/ventes/${String(venteId)}/echeances/wizard`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
  },

  chargesFixes: {
    list(
      params: { categorie?: string; actif?: 'true' | 'false' } = {},
    ): Promise<ChargesFixesListResponse> {
      return request<ChargesFixesListResponse>('/api/charges-fixes', { params });
    },
    create(body: ChargeFixeCreateBody): Promise<{ charge: ChargeFixe }> {
      return request<{ charge: ChargeFixe }>('/api/charges-fixes', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    update(id: number, body: ChargeFixeUpdateBody): Promise<{ charge: ChargeFixe }> {
      return request<{ charge: ChargeFixe }>(`/api/charges-fixes/${String(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    desactiver(id: number): Promise<{ charge: ChargeFixe }> {
      return request<{ charge: ChargeFixe }>(`/api/charges-fixes/${String(id)}/desactiver`, {
        method: 'PATCH',
      });
    },
    delete(id: number): Promise<void> {
      return request<void>(`/api/charges-fixes/${String(id)}`, { method: 'DELETE' });
    },
  },

  import: {
    saisie(body: {
      date_vente: string;
      contact_id: number | null;
      contact_nom: string | null;
      contact_societe: string | null;
      type_client: string;
      commercial: 'Adam' | 'Matteo';
      m2_centi: number | null;
      montant_ht_centimes: number;
      cout_unitaire_estime_centimes_par_m2: number | null;
      notes: string | null;
    }): Promise<ImportSaisieResponse> {
      return request<ImportSaisieResponse>('/api/import/historique/saisie', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    csvPreview(csv: string): Promise<CsvPreviewResponse> {
      return request<CsvPreviewResponse>('/api/import/historique/csv/preview', {
        method: 'POST',
        body: JSON.stringify({ csv }),
      });
    },
    csvCommit(body: CsvCommitBody): Promise<CsvCommitResponse> {
      return request<CsvCommitResponse>('/api/import/historique/csv/commit', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
  },
};

export { ApiError };
